import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import {
  clearContractLineAssets,
  fetchContractLineAssets,
} from 'store/contractLineAssetManager/operations'
import { selectContractLineDetails } from 'store/contractLineAssetManager/selector'
import { RootState, NetSuiteAsset } from 'types'

interface UseContractLineReturn {
  contractLineDetails: ReturnType<typeof selectContractLineDetails>
  isLoading: boolean
  selectedAsset: NetSuiteAsset | null
  setupModalOpen: boolean
  editModalOpen: boolean
  contractLineNSId: number
  setSelectedAsset: (asset: NetSuiteAsset | null) => void
  setSetupModalOpen: (open: boolean) => void
  setEditModalOpen: (open: boolean) => void
  backToSearch: () => void
  setupLicense: (asset: NetSuiteAsset) => void
  toggleSetupModal: () => void
  toggleEditModal: (asset: NetSuiteAsset) => void
  loadContractLine: () => void
}

export const useContractLine = (): UseContractLineReturn => {
  const [selectedAsset, setSelectedAsset] = useState<NetSuiteAsset | null>(null)
  const [setupModalOpen, setSetupModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)

  const { id } = useParams<{ id: string }>()
  const contractLineNSId = parseInt(id, 10)
  const dispatch = useDispatch()
  const history = useHistory()

  const { isLoading, contractLineDetails } = useSelector((state: RootState) => {
    const contractLineAssetsState = state.contractLineAssetManager
    return {
      contractLineDetails: selectContractLineDetails(
        state.contractLineAssetManager,
        contractLineNSId,
      ),
      isLoading: contractLineAssetsState?.isLoading,
    }
  })

  useEffect(() => {
    if (
      contractLineNSId &&
      (contractLineDetails.contractLineId === undefined ||
        contractLineDetails?.contractLineId !== contractLineNSId)
    ) {
      dispatch(fetchContractLineAssets(contractLineNSId))
    }
  }, [contractLineNSId, dispatch, contractLineDetails.contractLineId])

  const backToSearch = useCallback(() => {
    dispatch(clearContractLineAssets())
    history.push('/css-admin/contractLine')
  }, [history, dispatch])

  const setupLicense = useCallback((asset: NetSuiteAsset) => {
    setSelectedAsset(asset)
    setSetupModalOpen(true)
  }, [])

  const toggleSetupModal = useCallback(() => {
    setSetupModalOpen(prev => !prev)
  }, [])

  const loadContractLine = useCallback(() => {
    if (contractLineNSId) {
      dispatch(fetchContractLineAssets(contractLineNSId))
    }
  }, [dispatch, contractLineNSId])

  const toggleEditModal = useCallback(
    (asset: NetSuiteAsset) => {
      if (!editModalOpen) {
        setSelectedAsset(asset || null)
      } else {
        setSelectedAsset(null)
        // Reload contract line data when closing the modal
        loadContractLine()
      }
      setEditModalOpen(prev => !prev)
    },
    [editModalOpen, loadContractLine],
  )

  return {
    contractLineDetails,
    isLoading,
    selectedAsset,
    setupModalOpen,
    editModalOpen,
    contractLineNSId,
    setSelectedAsset,
    setSetupModalOpen,
    setEditModalOpen,
    backToSearch,
    setupLicense,
    toggleSetupModal,
    toggleEditModal,
    loadContractLine,
  }
}
